import pluralize from 'pluralize';

const PDF = '/images/fileTypes/pdf.svg'
const IMAGE = '/images/fileTypes/image.svg'
const PPT = '/images/fileTypes/ppt.svg'
const DOC = '/images/fileTypes/doc.svg'
const XLS = '/images/fileTypes/excel.svg'
const AUDIO = '/images/fileTypes/audio.svg'
const LINK = '/images/fileTypes/link.svg'
const VIDEO = '/images/fileTypes/video.svg'
const DEFAULT = '/images/fileTypes/default.svg'

export const FileService = {

  formatSize: (bytes: number) => {
    if (bytes >= 1000000000) {
      return (bytes / 1000000000).toFixed(2) + " GB";
    }
    if (bytes >= 1000000) {
      return (bytes / 1000000).toFixed(2) + " MB";
    }
    return (bytes / 1000).toFixed(2) + " KB";
  },


  formatDuration: (timeSpent: number, format: 'short' | 'long' = 'long') => {
    const SECOND = format === 'short' ? 's' : ' Seconds';
    const MINUTE = format === 'short' ? 'm' : ' Minutes';

    let timeSpentValue = `${FileService.padNumber(timeSpent, 1)}${SECOND}`;

    if (timeSpent >= 60) {
      const minutes = Math.round(timeSpent / 60);
      timeSpentValue = `${FileService.padNumber(minutes, 1)}${format === 'short' ? MINUTE : pluralize(MINUTE, minutes)}`;
    }

    return timeSpentValue;
  },

  padNumber: (number = 0, pad: number) => {
    number = Math.ceil(number);

    const length = pad - String(number).length + 1;
    let padding = "";
    if (length > 0) {
      padding = Array(pad - String(number).length + 1).join("0");
    }
    return padding + number;
  },

  getFileType: (metadata: { content_type: string;[key: string]: any }) => {
    if(!metadata){
      return 'FILE';
    }

    switch (metadata.content_type) {
      case "application/pdf":
        return "PDF";
      case "video/mp4":
        return "VIDEO";
      case "image/gif":
      case "image/tiff":
      case "image/png":
      case "image/bmp":
      case "image/jpeg":
      case "image/svg+xml":
        return "IMAGE";
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        return "PPTX";
      case "application/vnd.openxmlformats-officedocument.presentationml.slideshow":
        return "PPSX";
      case "application/vnd.openxmlformats-officedocument.presentationml.template":
      case "application/vnd.ms-powerpoint.addin.macroEnabled.12":
      case "application/vnd.ms-powerpoint.presentation.macroEnabled.12":
      case "application/vnd.ms-powerpoint.template.macroEnabled.12":
      case "application/vnd.ms-powerpoint.slideshow.macroEnabled.12":
      case "application/vnd.ms-powerpoint":
        return "PPT";
      case "application/msword":
        return "DOC";
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.template":
      case "application/vnd.ms-word.template.macroEnabled.12":
        return "DOCX";
      case "application/vnd.ms-excel":
        return "XLS"
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.template":
      case "application/vnd.ms-excel.sheet.macroEnabled.12":
      case "application/vnd.ms-excel.template.macroEnabled.12":
      case "application/vnd.ms-excel.addin.macroEnabled.12":
      case "application/vnd.ms-excel.sheet.binary.macroEnabled.12":
        return "XLSX"
      case "application/vnd.google-apps.document":
        return "G-DOC"
      case "application/vnd.google-apps.presentation":
        return "G-SLIDES"
      case "application/vnd.google-apps.spreadsheet":
        return "G-SHEET"
      case "application/vnd.google-apps.form":
        return "G-FORM"
      case "application/vnd.google-apps.drawing":
        return "G-DRAWING"
      case "application/vnd.google-apps.map":
        return "G-MAP"
      case "application/vnd.google-apps.sites":
        return "G-SITES"
      case "application/url":
        if (metadata.external_video_provider) {
          if (metadata.external_video_provider === "youtube") {
            return "YOUTUBE";
          } else if (metadata.external_video_provider === "vimeo") {
            return "VIMEO";
          } else if (metadata.external_video_provider === "wistia") {
            return "WISTIA";
          }
          else if (metadata.external_video_provider === "vidyard") {
            return "VIDYARD";
          }
        }
        return "LINK";
      case 'audio/mp3':
      case 'audio/mpeg':
      case 'audio/aiff':
      case 'audio/flac':
      case 'audio/x-aiff':
        return 'AUDIO';
      case 'application/zip':
      case 'application/x-zip-compressed':
        if (metadata.html_application) {
          return "HTML APP";
        } else if (metadata.scorm) {
          return "SCORM"
        }
        return 'ZIP';
      case 'application/cleverstory':
          return "INTERACTIVE";
      default:
        return "FILE";
    }
  },

  getFileTypeIcon: (metadata: { content_type: string;[key: string]: any }) => {
    const fileType = FileService.getFileType(metadata)
    switch (fileType) {
      case "PDF":
        return PDF;
      case "VIDEO":
        return VIDEO;
      case "IMAGE":
        return IMAGE;
      case "PPT":
      case "PPTX":
        return PPT;
      case "DOC":
      case "DOCX":
        return DOC;
      case "XLS":
      case "XLSX":
        return XLS;
      case "YOUTUBE":
      case "VIMEO":
      case "WISTIA":
      case "VIDYARD":
        return VIDEO;
      case "LINK":
        return LINK;
      case 'AUDIO':
        return AUDIO;
      default:
        return DEFAULT;
    }
  },

  hasPageAnalytics: (metadata: { content_type: string;[key: string]: any }) => {
    switch (metadata.content_type) {
      case "application/pdf":
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      case "application/vnd.openxmlformats-officedocument.presentationml.template":
      case "application/vnd.openxmlformats-officedocument.presentationml.slideshow":
      case "application/vnd.ms-powerpoint.addin.macroEnabled.12":
      case "application/vnd.ms-powerpoint.presentation.macroEnabled.12":
      case "application/vnd.ms-powerpoint.template.macroEnabled.12":
      case "application/vnd.ms-powerpoint.slideshow.macroEnabled.12":
      case "application/vnd.ms-powerpoint":
      case "application/msword":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.template":
      case "application/vnd.ms-word.template.macroEnabled.12":
      case "application/vnd.google-apps.document":
      case "application/vnd.google-apps.presentation":
        return true;

      default:
        return false;
    }
  },

  hasVideoAnalytics: (metadata: { content_type: string;[key: string]: any }) => {
    switch (metadata.content_type) {
      case "video/mp4":
        return true;

      case "application/url":
        if (metadata.external_video_provider) {
          if (
            metadata.external_video_provider === "youtube" ||
            metadata.external_video_provider === "vimeo" ||
            metadata.external_video_provider === "wistia" ||
            metadata.external_video_provider === "vidyard"
          ) {
            return true;
          }
        }

        return false;

      default:
        return false;
    }
  },

  hasAudioAnalytics: (metadata: { content_type: string;[key: string]: any }) => {
    switch (metadata.content_type) {
      case 'audio/mp3':
      case 'audio/mpeg':
      case 'audio/aiff':
      case 'audio/flac':
      case 'audio/x-aiff':
        return true;

      default:
        return false;
    }
  },

  hasAnalytics: (metadata: { content_type: string;[key: string]: any }) => {
    return (
      FileService.hasVideoAnalytics(metadata) ||
      FileService.hasPageAnalytics(metadata) ||
      FileService.hasAudioAnalytics(metadata)
    );
  },

  getFileExtension: (fileName: string) => {
    return fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length) || fileName;
  },


}